/**
 * 列配置工具
 * 优先读取管理后台发布的动态配置，否则回退到硬编码
 */

import {
  getPageSettings,
  getPublishedColumns,
  getPublishedPageConfig,
} from '@/services/pageConfigService'

/** @deprecated 仅作硬编码兜底 */
export const featureStatusConfig = {
  1: { isUnderConstruction: false, emptyStateText: '功能建设中' },
  3: { isUnderConstruction: false, emptyStateText: '功能建设中' },
}

export function isUnderConstruction(labelGroup) {
  const settings = getPageSettings(labelGroup)
  if (settings) return !!settings.underConstruction
  const config = featureStatusConfig[labelGroup]
  return config ? config.isUnderConstruction : false
}

export function getEmptyStateText(labelGroup) {
  const settings = getPageSettings(labelGroup)
  if (settings?.emptyStateText) return settings.emptyStateText
  const config = featureStatusConfig[labelGroup]
  return config ? config.emptyStateText : '暂无数据'
}

export function getPageMeta(labelGroup) {
  const settings = getPageSettings(labelGroup)
  if (settings) {
    return {
      dataSource: settings.dataSource || '',
      titleField: settings.titleField || 'title',
      apiDetail: settings.apiDetail || '',
    }
  }
  const fallback = {
    1: { dataSource: 'GlobeNewswire', titleField: 'description' },
    2: { dataSource: '国家药品监督管理局医疗器械板块', titleField: 'policies_title' },
    3: { dataSource: '中国医疗器械信息网', titleField: 'bid_title' },
    4: { dataSource: 'PubMed', titleField: 'title' },
    5: { dataSource: '美国FDA医疗器械查询数据库', titleField: 'productName' },
    6: { dataSource: 'ClinicalTrials.gov', titleField: 'title' },
  }
  return fallback[Number(labelGroup)] || { dataSource: '', titleField: 'title' }
}

export function getDetailColumns(labelGroup) {
  const cfg = getPublishedPageConfig()
  const raw = cfg?.columns?.[Number(labelGroup)]
  if (raw) {
    return raw
      .filter((c) => c.detailVisible !== false)
      .sort((a, b) => a.sort - b.sort)
      .map((c) => ({
        key: c.key,
        label: c.label,
        visible: true,
        maxWidth: c.maxWidth ?? undefined,
        type: c.cellType === 'list' ? 'list' : undefined,
        cellType: c.cellType || 'text',
      }))
  }
  return getColumnsByLabelGroupHardcoded(labelGroup)
}

export function getColumnsByLabelGroup(labelGroup) {
  const published = getPublishedColumns(labelGroup)
  const cols = published?.length ? published : getColumnsByLabelGroupHardcoded(labelGroup)
  return ensureTimeColumnsLast(cols)
}

/** 所有列表末尾固定「数据创建时间」「数据更新时间」 */
function ensureTimeColumnsLast(cols) {
  const list = Array.isArray(cols) ? cols : []
  const rest = list.filter((c) => c.key !== 'create_time' && c.key !== 'update_time')
  return [
    ...rest,
    { key: 'create_time', label: '数据创建时间', visible: true, width: 150 },
    { key: 'update_time', label: '数据更新时间', visible: true, width: 150 },
  ]
}

function getColumnsByLabelGroupHardcoded(labelGroup) {
  const labelGroupNum = Number(labelGroup)
  const columnsMap = {
    1: [
      { key: 'action_type', label: '事件类别', visible: true, maxWidth: 90 },
      { key: 'disease_area', label: '疾病领域', visible: true, maxWidth: 90 },
      { key: 'company_name', label: '企业名称', visible: true, maxWidth: 120 },
      { key: 'title', label: '新闻标题', visible: true, maxWidth: 650 },
      { key: 'description', label: '摘要', visible: true, maxWidth: 450 },
      { key: 'publish_date', label: '发布时间', visible: true },
      { key: 'device_type', label: '设备类型', visible: true },
      { key: 'nation_area', label: '地区', visible: true },
      { key: 'resource_link', label: '原始链接', visible: true },
    ],
    2: [
      { key: 'policies_title', label: '政策标题', visible: true, maxWidth: 450 },
      { key: 'policies_sn', label: '编号/文号', visible: true },
      { key: 'publish_date', label: '发布日期', visible: true },
      { key: 'effective_date', label: '生效日期/实施日期', visible: true },
      { key: 'description', label: '关键政策摘要/要点', visible: true },
      { key: 'nation_area', label: '地区', visible: true, maxWidth: 75 },
      { key: 'policies_category', label: '政策分类', visible: true },
      { key: 'resource_link', label: '原文链接/来源', visible: true },
    ],
    3: [
      { key: 'bid_title', label: '项目名称/公告标题', visible: true, maxWidth: 450 },
      { key: 'bid_sn', label: '招标编号/公告编号', visible: true, maxWidth: 150 },
      { key: 'publish_date', label: '公告日期/发布时间', visible: true },
      { key: 'bid_units', label: '采购单位/医院/机构', visible: true, maxWidth: 200 },
      { key: 'province_area', label: '地区', visible: true, maxWidth: 75 },
      { key: 'bid_methods', label: '采购方式/招标方式', visible: true, maxWidth: 150 },
      { key: 'disease_area', label: '疾病领域', visible: true, maxWidth: 60 },
      { key: 'device_type', label: '设备类型', visible: true, maxWidth: 200 },
      { key: 'description', label: '摘要', visible: true },
      { key: 'resource_link', label: '原文链接/公告链接', visible: true },
    ],
    4: [
      { key: 'tech_areas_tag', label: '技术类型', visible: true, maxWidth: 90 },
      { key: 'nation_area', label: '地区', visible: false, maxWidth: 75 },
      { key: 'device_type', label: '设备类型', visible: false },
      { key: 'disease_states_tag', label: '疾病领域标签', visible: true, maxWidth: 90 },
      { key: 'project_title', label: '标题', visible: true, maxWidth: 700 },
      { key: 'paper_authors', label: '作者列表', visible: true, maxWidth: 100, type: 'list' },
      { key: 'indication', label: '适应症', visible: true, maxWidth: 300 },
      { key: 'untils_name', label: '机构名称', visible: false },
      { key: 'paper_abstructs', label: '关键信息摘要', visible: true, maxWidth: 550 },
      { key: 'publish_date', label: '时间', visible: true },
      { key: 'research_phase', label: '研究阶段', visible: true },
      { key: 'resource_link', label: '原始链接', visible: true },
    ],
    5: [
      { key: 'disease_states', label: '疾病领域', visible: true, maxWidth: 100 },
      { key: 'device_type', label: '设备类型', visible: true, maxWidth: 250 },
      { key: 'product_name', label: '产品名称', visible: true, maxWidth: 250 },
      { key: 'device_classification_name', label: '产品分类名称', visible: true, maxWidth: 200 },
      { key: 'registrant', label: '备案人', visible: true, maxWidth: 250 },
      { key: 'indication', label: '适应症', visible: true, maxWidth: 250 },
      { key: 'approval_date', label: '批准/备案日期', visible: true },
      { key: 'first_approval_date', label: '获批日期', visible: true },
      { key: 'registration_number', label: '注册证编号', visible: true },
      { key: 'management_category', label: '管理类别', visible: true },
      { key: 'resource_link', label: '原文链接', visible: true },
      { key: 'nation_area', label: '地区', visible: true, maxWidth: 75 },
    ],
    6: [
      { key: 'indication', label: '疾病症状', visible: true, maxWidth: 200 },
      { key: 'device_name', label: '设备', visible: true, maxWidth: 200 },
      { key: 'lead_sponsor', label: '申办方', visible: true, maxWidth: 200 },
      { key: 'project_title', label: '标题', visible: true, maxWidth: 350 },
      { key: 'study_type', label: '实验类型', visible: true, maxWidth: 120 },
      { key: 'status', label: '研究状态', visible: true, maxWidth: 120 },
      { key: 'start_date', label: '开始时间', visible: true, maxWidth: 120 },
      { key: 'completion_date', label: '结束时间', visible: true, maxWidth: 120 },
      { key: 'resource_link', label: '原始链接', visible: true },
      { key: 'study_number', label: '编号', visible: true, maxWidth: 220 },
      { key: 'experiment_phase', label: '实验阶段', visible: true },
      { key: 'disease_areas_tag', label: '疾病领域', visible: true },
      { key: 'nation_area', label: '地区', visible: true, maxWidth: 75 },
      { key: 'device_type', label: '设备类型', visible: true },
      { key: 'experiment_type', label: '是否注册性实验', visible: false },
    ],
  }
  return columnsMap[labelGroupNum] || []
}
